import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { PlatformWorkflowStrip } from '../components/platform/PlatformWorkflowStrip';
import {
  createMeasurementPlan,
  evaluateMeasurementPlan,
  freezePolicyMeasurement,
  getLatestMeasurementEvaluation,
  getMeasurementPlan,
  getMeasurementReviewQueue,
  getPolicyMeasurementReadiness,
  submitMeasurementAnnotation,
  type MeasurementEvaluationArtifact,
  type MeasurementReviewEvent,
  type MeasurementReviewItem,
  type MeasurementPlan,
  type PolicyMeasurementReadinessResponse,
} from '../services/accuracyApi';
import { reportsApi, type SafetyCaseRecord } from '../services/reportsApi';

function queryValue(search: string, key: string): string {
  return String(new URLSearchParams(search).get(key) || '').trim();
}

function localInput(date: Date): string {
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return shifted.toISOString().slice(0, 16);
}

function utc(value: string): string {
  return new Date(value).toISOString();
}

function display(value: number | null | undefined, digits = 3): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : '—';
}

function reasonLabel(value: string): string {
  return value.replace(/_/g, ' ');
}

function readScopedValue(search: string, key: string, storageKey: string): string {
  return queryValue(search, key) || String(localStorage.getItem(storageKey) || '').trim();
}

/** Map Measure policy_type to the workflow risk token used by Safety Cases. */
export function measurePolicyTypeToWorkflowRisk(policyType?: string | null): string {
  const value = String(policyType || '').trim().toLowerCase();
  if (!value) return '';
  if (value.includes('near_moving') || value.includes('forklift_proximity') || value === 'near_miss') {
    return 'near_miss';
  }
  if (value.includes('zone')) return 'zone_violation';
  return value;
}

export type MeasureSafetyCaseMatch = {
  safetyCaseId: string;
  workflowId: string;
  reportId?: string | null;
};

/** Prefer the live non-archived Safety Case for this Measure camera/zone. */
export function matchSafetyCaseForMeasureScope(args: {
  cases: SafetyCaseRecord[];
  cameraId?: string | null;
  zoneId?: string | null;
  policyType?: string | null;
  deviceId?: string | null;
}): MeasureSafetyCaseMatch | null {
  const cameraId = String(args.cameraId || '').trim();
  const zoneId = String(args.zoneId || '').trim();
  const deviceId = String(args.deviceId || '').trim();
  const risk = measurePolicyTypeToWorkflowRisk(args.policyType);
  if (!cameraId && !zoneId) return null;

  const active = args.cases.filter((item) => {
    const status = String(item.safety_case_status || item.status || '').trim().toLowerCase();
    return status !== 'archived';
  });

  const score = (item: SafetyCaseRecord): number => {
    const workflowId = String(item.workflow_id || '').trim();
    const parts = workflowId.split('|').map((part) => part.trim()).filter(Boolean);
    const scope = (item.scope && typeof item.scope === 'object') ? item.scope as Record<string, unknown> : {};
    const scopeCamera = String(scope.camera_id || '').trim();
    const scopeZone = String(scope.zone_id || '').trim();
    let points = 0;
    if (cameraId && (parts.includes(cameraId) || scopeCamera === cameraId)) points += 8;
    if (zoneId && (parts.includes(zoneId) || scopeZone === zoneId)) points += 4;
    if (risk && parts[0] === risk) points += 3;
    if (deviceId && parts.includes(deviceId)) points += 1;
    if (String(item.active_draft_report_id || '').trim()) points += 1;
    return points;
  };

  const ranked = [...active]
    .map((item) => ({ item, points: score(item) }))
    .filter((row) => row.points >= (cameraId ? 8 : 4))
    .sort((a, b) => b.points - a.points);
  const best = ranked[0]?.item;
  if (!best) return null;
  const safetyCaseId = String(best.safety_case_id || best.finding_id || '').trim();
  const workflowId = String(best.workflow_id || '').trim();
  if (!safetyCaseId || !workflowId) return null;
  return {
    safetyCaseId,
    workflowId,
    reportId: String(best.active_draft_report_id || '').trim() || null,
  };
}

/** After MET freeze, open Draft Safety Scan — never device preflight commissioning. */
export function buildMeasureContinueToScanPath(args: {
  search: string;
  policyId: string;
  policyType?: string | null;
  cameraId?: string | null;
  zoneId?: string | null;
  deviceId?: string | null;
  safetyCaseId?: string | null;
  workflowId?: string | null;
  reportId?: string | null;
}): string {
  const params = new URLSearchParams();
  const orgId = readScopedValue(args.search, 'org_id', 'ui.scope.org_id');
  const siteId = readScopedValue(args.search, 'site_id', 'ui.scope.site_id');
  if (orgId) params.set('org_id', orgId);
  if (siteId) params.set('site_id', siteId);
  if (args.policyId) params.set('policy_id', args.policyId);
  if (args.policyType) params.set('policy_type', String(args.policyType));
  if (args.cameraId) params.set('camera_id', String(args.cameraId));
  if (args.zoneId) params.set('zone_id', String(args.zoneId));
  if (args.deviceId) params.set('device_id', String(args.deviceId));
  if (args.workflowId) params.set('workflow_id', String(args.workflowId));
  if (args.safetyCaseId) {
    params.set('safety_case_id', String(args.safetyCaseId));
    params.set('case_id', String(args.safetyCaseId));
  }
  if (args.reportId) params.set('report_id', String(args.reportId));
  params.set('surface', 'package');
  const suffix = params.toString();
  return suffix ? `/acceptance/proof?${suffix}` : '/acceptance/proof';
}

const MeasurePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const policyId = queryValue(location.search, 'policy_id');
  const requestedPlanId = queryValue(location.search, 'plan_id');
  const now = useMemo(() => new Date(), []);

  const [policyData, setPolicyData] = useState<PolicyMeasurementReadinessResponse | null>(null);
  const [plan, setPlan] = useState<MeasurementPlan | null>(null);
  const [queue, setQueue] = useState<MeasurementReviewItem[]>([]);
  const [artifact, setArtifact] = useState<MeasurementEvaluationArtifact | null>(null);
  const [frozen, setFrozen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState<'plan' | 'refresh' | 'review' | 'evaluate' | 'freeze' | 'continue' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [queueBlocker, setQueueBlocker] = useState<string | null>(null);
  const [reviewItem, setReviewItem] = useState<MeasurementReviewItem | null>(null);
  const [reviewOutcome, setReviewOutcome] = useState<'present' | 'absent' | 'insufficient'>('absent');
  const [eventDrafts, setEventDrafts] = useState([
    { eventKey: 'event-1', startMs: '0', endMs: '1000', subjectRef: '' },
  ]);

  const [populationStart, setPopulationStart] = useState(localInput(new Date(now.getTime() + 60 * 60_000)));
  const [populationEnd, setPopulationEnd] = useState(localInput(new Date(now.getTime() + 4 * 60 * 60_000)));
  const [seed, setSeed] = useState('measure-v2-prospective-1');
  const [clipDuration, setClipDuration] = useState('90');
  const [targetCount, setTargetCount] = useState('30');
  const [zoneId, setZoneId] = useState(queryValue(location.search, 'zone_id'));
  const [configHash, setConfigHash] = useState(queryValue(location.search, 'config_hash'));
  const [modelHash, setModelHash] = useState(queryValue(location.search, 'model_hash'));
  const [thresholdHash, setThresholdHash] = useState(queryValue(location.search, 'threshold_hash'));
  const [runtimeVersion, setRuntimeVersion] = useState(queryValue(location.search, 'runtime_version'));

  const loadWorkflow = useCallback(async (planId: string) => {
    setWorking('refresh');
    setError(null);
    setQueueBlocker(null);
    try {
      const planResponse = await getMeasurementPlan(planId);
      setPlan(planResponse.plan);
      const [queueResult, evaluationResult] = await Promise.allSettled([
        getMeasurementReviewQueue(planId),
        getLatestMeasurementEvaluation(planId),
      ]);
      if (queueResult.status === 'fulfilled') {
        setQueue(queueResult.value.items);
      } else {
        setQueue([]);
        const response = (queueResult.reason as any)?.response?.data;
        setQueueBlocker(String(response?.error || response?.detail || 'review_assignment_or_authentication_required'));
      }
      setArtifact(evaluationResult.status === 'fulfilled' ? evaluationResult.value.artifact : null);
    } catch (err: any) {
      setError(String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'measurement_plan_load_failed'));
      setPlan(null);
      setQueue([]);
      setArtifact(null);
    } finally {
      setWorking(null);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    if (!policyId) {
      setLoading(false);
      return () => { active = false; };
    }
    void getPolicyMeasurementReadiness(policyId)
      .then((response) => {
        if (!active) return;
        setPolicyData(response);
        setFrozen(String(response.readiness?.current_status || '').toUpperCase() === 'MET');
        if (requestedPlanId) void loadWorkflow(requestedPlanId);
        else setLoading(false);
      })
      .catch((err: any) => {
        if (!active) return;
        setError(String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'policy_measurement_load_failed'));
        setLoading(false);
      });
    return () => { active = false; };
  }, [loadWorkflow, policyId, requestedPlanId]);

  const schedule = plan?.plan_payload.schedule || [];
  const admittedOrdinals = new Set(queue.map((item) => item.sample_ordinal));
  const coverage = plan?.plan_payload.contract.strata.map((stratum) => {
    const planned = schedule.filter((item) => item.stratum === stratum.name).length;
    const admitted = queue.filter((item) => item.evidence.scheduled_window?.stratum === stratum.name).length;
    return { name: stratum.name, planned, admitted };
  }) || [];
  const ownComplete = queue.filter((item) => Boolean(item.own_submission)).length;
  const dualComplete = queue.filter((item) => item.review_complete).length;
  const payload = artifact?.artifact_payload;
  const metrics = payload?.metrics || artifact?.metrics;
  const failedChecks = Object.entries(payload?.threshold_checks || {})
    .filter(([, ok]) => !ok)
    .map(([key]) => key);
  const staleReason = policyData?.binding_stale_reason;
  const scanBlockers = [
    ...(staleReason ? [staleReason] : []),
    ...failedChecks,
    ...(payload?.exclusions || []).map((item) => `${item.reason}:${item.clip_id}`),
  ];

  const createPlan = async () => {
    const policy = policyData?.policy;
    const target = Number(targetCount);
    const duration = Number(clipDuration);
    if (!policy) return;
    if (!zoneId || !configHash || !modelHash || !thresholdHash || !runtimeVersion) {
      setError('Exact zone, config hash, model hash, threshold hash, and runtime version are required.');
      return;
    }
    if (!Number.isInteger(target) || target < 3 || !Number.isInteger(duration) || duration <= 0) {
      setError('Target must be at least 3 and clip duration must be a positive whole number.');
      return;
    }
    const start = new Date(populationStart);
    const end = new Date(populationEnd);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || start <= now || end <= start) {
      setError('Population window must be valid, UTC-convertible, and prospective.');
      return;
    }
    const width = end.getTime() - start.getTime();
    const boundaries = [start, new Date(start.getTime() + width / 3), new Date(start.getTime() + 2 * width / 3), end];
    const names = ['early', 'middle', 'late'];
    const base = Math.floor(target / 3);
    const quotas = Object.fromEntries(names.map((name, index) => [name, base + (index < target % 3 ? 1 : 0)]));
    setWorking('plan');
    setError(null);
    setNotice(null);
    try {
      const response = await createMeasurementPlan({
        policyId,
        policyVersion: Number(policy.version || 0),
        configHash,
        modelHash,
        thresholdHash,
        runtimeIdentity: { version: runtimeVersion },
        cameraId: String(policy.camera_id || ''),
        zoneId,
        populationStart: utc(populationStart),
        populationEnd: utc(populationEnd),
        strata: names.map((name, index) => ({ name, start: boundaries[index].toISOString(), end: boundaries[index + 1].toISOString() })),
        seed,
        clipDurationSeconds: duration,
        targetClipCount: target,
        quotas,
      });
      setPlan(response.plan);
      setQueue([]);
      setArtifact(null);
      setFrozen(false);
      setNotice('Prospective stratified plan is frozen. Edge collection now follows its scheduled windows.');
      const params = new URLSearchParams(location.search);
      params.set('plan_id', response.plan.id);
      navigate({ pathname: '/accuracy', search: params.toString() }, { replace: true });
    } catch (err: any) {
      setError(String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'measurement_plan_create_failed'));
    } finally {
      setWorking(null);
    }
  };

  const submitReview = async () => {
    if (!reviewItem) return;
    let events: MeasurementReviewEvent[] = [];
    if (reviewOutcome === 'present') {
      const invalid = eventDrafts.some((draft) => {
        const startMs = Number(draft.startMs);
        const endMs = Number(draft.endMs);
        return !draft.eventKey.trim() || !Number.isInteger(startMs) || !Number.isInteger(endMs) || startMs < 0 || endMs <= startMs;
      });
      if (invalid || new Set(eventDrafts.map((draft) => draft.eventKey.trim())).size !== eventDrafts.length) {
        setError('Present events require a key and valid millisecond start/end boundaries.');
        return;
      }
      events = eventDrafts.map((draft) => ({
        event_key: draft.eventKey.trim(),
        start_ms: Number(draft.startMs),
        end_ms: Number(draft.endMs),
        subject_ref: draft.subjectRef.trim() || null,
      }));
    }
    setWorking('review');
    setError(null);
    try {
      await submitMeasurementAnnotation(reviewItem.id, { outcome: reviewOutcome, events });
      setReviewItem(null);
      setNotice('Immutable annotation submitted for the authenticated reviewer.');
      await loadWorkflow(plan!.id);
    } catch (err: any) {
      setError(String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'annotation_submit_failed'));
    } finally {
      setWorking(null);
    }
  };

  const evaluate = async () => {
    if (!plan) return;
    setWorking('evaluate');
    setError(null);
    try {
      const response = await evaluateMeasurementPlan(plan.id);
      setArtifact(response.artifact);
      setFrozen(false);
      setNotice(`Server evaluation completed: ${response.artifact.artifact_payload.status}.`);
    } catch (err: any) {
      setError(String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'measurement_evaluation_failed'));
    } finally {
      setWorking(null);
    }
  };

  const freeze = async () => {
    if (!artifact) return;
    setWorking('freeze');
    setError(null);
    try {
      const response = await freezePolicyMeasurement(policyId, artifact.id);
      setFrozen(String(response.readiness?.current_status || '').toUpperCase() === 'MET');
      setNotice('Immutable v2 measurement freeze created from the evaluation artifact.');
    } catch (err: any) {
      const response = err?.response?.data;
      setError([response?.error, response?.detail].filter(Boolean).join(': ') || err?.message || 'measurement_freeze_failed');
    } finally {
      setWorking(null);
    }
  };

  const continueToScan = async () => {
    const cameraId = policyData?.policy?.camera_id
      || plan?.plan_payload?.contract?.identity?.camera_id
      || null;
    const resolvedZoneId = zoneId
      || plan?.plan_payload?.contract?.identity?.zone_id
      || null;
    const deviceId = policyData?.policy?.device_id || null;
    const policyType = policyData?.policy?.policy_type || null;
    setWorking('continue');
    setError(null);
    try {
      const listed = await reportsApi.listSafetyCases({ limit: 200 });
      const cases = Array.isArray(listed.safety_cases)
        ? listed.safety_cases
        : (Array.isArray(listed.items) ? listed.items : []);
      const matched = matchSafetyCaseForMeasureScope({
        cases,
        cameraId,
        zoneId: resolvedZoneId,
        policyType,
        deviceId,
      });
      if (!matched) {
        setError(
          'No matching Safety Case found for this camera/zone. Open Safety Cases and create or select the near-moving case, then retry Continue.',
        );
        return;
      }
      navigate(buildMeasureContinueToScanPath({
        search: location.search,
        policyId,
        policyType,
        cameraId,
        zoneId: resolvedZoneId,
        deviceId,
        safetyCaseId: matched.safetyCaseId,
        workflowId: matched.workflowId,
        reportId: matched.reportId,
      }));
    } catch (err: any) {
      setError(String(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'safety_case_lookup_failed'));
    } finally {
      setWorking(null);
    }
  };

  if (!policyId) {
    return (
      <Stack spacing={2}>
        <Typography variant="h4">Measure detection readiness</Typography>
        <Alert severity="info">Open a configured policy first. Measure binds evidence to its exact deployed identity.</Alert>
        <Button variant="contained" onClick={() => navigate('/policies')}>Open Policies</Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1100, pb: 5 }}>
      <Button variant="text" sx={{ alignSelf: 'flex-start', px: 0 }} onClick={() => navigate('/policies')}>Back to Policies</Button>
      <PlatformWorkflowStrip currentStep="measure" />
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>Measure detection readiness</Typography>
        <Typography color="text.secondary">One evidence-backed sequence: plan, collect, blind review, evaluate, freeze, then Scan.</Typography>
      </Box>
      {loading ? <CircularProgress /> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      {notice ? <Alert severity="success">{notice}</Alert> : null}

      {policyData?.policy ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography sx={{ fontWeight: 800 }}>{policyData.policy.policy_type}</Typography>
              <Typography variant="body2" color="text.secondary">
                Policy {policyId} · camera {policyData.policy.camera_id || 'unbound'} · device {policyData.policy.device_id || 'unbound'} · v{policyData.policy.version}
              </Typography>
            </Box>
            <Chip color={frozen ? 'success' : 'warning'} label={frozen ? 'SCAN READY' : 'NOT READY'} />
          </Stack>
          {staleReason ? <Alert severity="warning" sx={{ mt: 1.5 }}>Exact stale reason: {staleReason}</Alert> : null}
        </Paper>
      ) : null}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>1. Define prospective collection</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The server freezes a seeded schedule before collection. The window is divided into three declared, non-overlapping strata.
          </Typography>
          {plan ? (
            <Stack spacing={1}>
              <Alert severity="success">Frozen plan {plan.id} · {plan.sampling_method} · hash {plan.plan_payload.plan_hash.slice(0, 12)}…</Alert>
              <Typography variant="body2">
                Population {new Date(plan.collection_window_start).toLocaleString()} – {new Date(plan.collection_window_end).toLocaleString()}
                {' · '}{plan.target_clip_count} clips × {plan.clip_duration_seconds}s · seed {plan.plan_payload.contract.seed}
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField fullWidth type="datetime-local" label="Population starts" InputLabelProps={{ shrink: true }} value={populationStart} onChange={(event) => setPopulationStart(event.target.value)} />
                <TextField fullWidth type="datetime-local" label="Population ends" InputLabelProps={{ shrink: true }} value={populationEnd} onChange={(event) => setPopulationEnd(event.target.value)} />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField fullWidth label="Deterministic seed" value={seed} onChange={(event) => setSeed(event.target.value)} />
                <TextField fullWidth label="Clip duration (seconds)" inputMode="numeric" value={clipDuration} onChange={(event) => setClipDuration(event.target.value)} />
                <TextField fullWidth label="Target clips" inputMode="numeric" value={targetCount} onChange={(event) => setTargetCount(event.target.value)} />
              </Stack>
              <Typography variant="subtitle2">Exact deployed identity</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField fullWidth label="Zone ID" value={zoneId} onChange={(event) => setZoneId(event.target.value)} />
                <TextField fullWidth label="Runtime version" value={runtimeVersion} onChange={(event) => setRuntimeVersion(event.target.value)} />
              </Stack>
              <TextField fullWidth label="Canonical config hash" value={configHash} onChange={(event) => setConfigHash(event.target.value)} />
              <TextField fullWidth label="Model hash" value={modelHash} onChange={(event) => setModelHash(event.target.value)} />
              <TextField fullWidth label="Threshold hash" value={thresholdHash} onChange={(event) => setThresholdHash(event.target.value)} />
              <Button variant="contained" disabled={Boolean(working)} onClick={() => void createPlan()}>
                {working === 'plan' ? 'Freezing plan…' : 'Create and freeze collection plan'}
              </Button>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>2. Collect scheduled clips</Typography>
          {!plan ? <Alert severity="info">Create the prospective plan first.</Alert> : (
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Chip label={`${queue.length}/${schedule.length} admitted`} color={queue.length === schedule.length && schedule.length > 0 ? 'success' : 'warning'} />
                {coverage.map((item) => <Chip key={item.name} variant="outlined" label={`${item.name}: ${item.admitted}/${item.planned}`} />)}
                <Button size="small" disabled={Boolean(working)} onClick={() => void loadWorkflow(plan.id)}>Refresh collection</Button>
              </Stack>
              <Stack spacing={0.75}>
                {schedule.map((item) => (
                  <Paper key={item.ordinal} variant="outlined" sx={{ p: 1 }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2">#{item.ordinal + 1} · {item.stratum} · {new Date(item.start).toLocaleString()}</Typography>
                      <Chip size="small" color={admittedOrdinals.has(item.ordinal) ? 'success' : 'default'} label={admittedOrdinals.has(item.ordinal) ? 'Admitted' : 'Scheduled'} />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>3. Blind immutable review</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            The queue is scoped to the authenticated reviewer and never exposes peer identity or labels.
          </Typography>
          {queueBlocker ? <Alert severity="warning" sx={{ mb: 1.5 }}>Exact blocker: {queueBlocker}. Two distinct authenticated reviewer assignments must already exist.</Alert> : null}
          {plan && queue.length === 0 && !queueBlocker ? <Alert severity="info">No admitted clips are in this reviewer’s queue yet.</Alert> : null}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>{ownComplete}/{queue.length} submitted by you · {dualComplete}/{queue.length} dual-review complete</Typography>
          <Stack spacing={1}>
            {queue.map((item) => (
              <Paper key={item.id} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>Clip #{item.sample_ordinal + 1} · {item.evidence.scheduled_window?.stratum || 'unstratified'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.evidence.content_sha256.slice(0, 12)}… · {item.review_complete ? 'both reviews complete' : 'peer review hidden or pending'}
                    </Typography>
                  </Box>
                  {item.own_submission
                    ? <Chip color="success" label={`Immutable: ${item.own_submission.outcome}`} />
                    : <Button variant="contained" size="small" disabled={!item.evidence.storage_playable} onClick={() => {
                      setReviewOutcome('absent');
                      setEventDrafts([{ eventKey: 'event-1', startMs: '0', endMs: '1000', subjectRef: '' }]);
                      setReviewItem(item);
                    }}>Play and annotate evidence</Button>}
                </Stack>
              </Paper>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>4. Compute server evaluation</Typography>
          <Button variant="contained" disabled={!plan || Boolean(working)} onClick={() => void evaluate()}>
            {working === 'evaluate' ? 'Computing…' : 'Evaluate admitted reviewed clips'}
          </Button>
          {payload && metrics ? (
            <Stack spacing={1.5} sx={{ mt: 2 }}>
              <Alert severity={payload.status === 'MET' ? 'success' : 'warning'}>Evaluation {artifact?.id} · {payload.status} · {payload.evaluator_version}</Alert>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`TP ${metrics.tp}`} /><Chip label={`FN ${metrics.fn}`} /><Chip label={`FP ${metrics.fp}`} /><Chip label={`TN empty clips ${metrics.tn_empty_clips}`} />
                <Chip label={`Usable ${metrics.usable_seconds}s`} /><Chip label={`Paired reviews ${metrics.paired_reviews}`} />
              </Stack>
              <Typography variant="body2">Precision {display(metrics.precision)} · 95% CI {display(metrics.precision_wilson_95?.lower)}–{display(metrics.precision_wilson_95?.upper)}</Typography>
              <Typography variant="body2">Recall {display(metrics.recall)} · 95% CI {display(metrics.recall_wilson_95?.lower)}–{display(metrics.recall_wilson_95?.upper)}</Typography>
              <Typography variant="body2">False alerts / usable hour {display(metrics.false_alerts_per_usable_hour)} · Cohen’s κ {display(metrics.inter_annotator_kappa)}</Typography>
              <Typography variant="body2">Coverage by stratum: {Object.entries(metrics.strata).map(([name, count]) => `${name} ${count}`).join(' · ') || 'none'}</Typography>
              <Divider />
              <Typography variant="subtitle2">Sufficiency and gates</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {Object.entries(payload.threshold_checks).map(([key, ok]) => <Chip key={key} color={ok ? 'success' : 'warning'} label={`${reasonLabel(key)}: ${ok ? 'pass' : 'blocked'}`} />)}
              </Stack>
              <Typography variant="subtitle2">Exclusions</Typography>
              {payload.exclusions.length
                ? payload.exclusions.map((item) => <Typography key={`${item.clip_id}-${item.reason}`} variant="body2">{item.clip_id}: {item.reason}</Typography>)
                : <Typography variant="body2" color="text.secondary">No exclusions.</Typography>}
            </Stack>
          ) : null}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>5. Freeze and continue to Scan</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Freeze submits only the server-owned evaluation artifact ID. Metrics, hashes, reviewer identity, and duration cannot be supplied here.
          </Typography>
          {!frozen && scanBlockers.length > 0 ? (
            <Alert severity="warning" sx={{ mb: 1.5 }}>Exact blockers: {scanBlockers.join(' · ')}</Alert>
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button variant="contained" disabled={!artifact || payload?.status !== 'MET' || Boolean(working)} onClick={() => void freeze()}>
              {working === 'freeze' ? 'Freezing…' : 'Freeze evaluation artifact'}
            </Button>
            <Button
              variant="outlined"
              disabled={!frozen || Boolean(working)}
              onClick={() => void continueToScan()}
            >
              {working === 'continue' ? 'Opening Safety Scan…' : 'Continue to Safety Scan'}
            </Button>
          </Stack>
          {frozen ? (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              Scan readiness: MET. Continue opens the matching Safety Case Draft Scan for this camera/zone — not device preflight.
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <Dialog open={Boolean(reviewItem)} onClose={() => setReviewItem(null)} fullWidth maxWidth="md">
        <DialogTitle>Blind evidence review</DialogTitle>
        <DialogContent dividers>
          {reviewItem ? (
            <Stack spacing={2}>
              <Box component="video" src={reviewItem.evidence.storage_uri} controls data-testid="measurement-evidence-video" sx={{ width: '100%', maxHeight: 480, bgcolor: 'black' }} />
              <Typography variant="caption">Evidence SHA-256 {reviewItem.evidence.content_sha256}</Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {(['absent', 'present', 'insufficient'] as const).map((outcome) => (
                  <Button key={outcome} variant={reviewOutcome === outcome ? 'contained' : 'outlined'} onClick={() => setReviewOutcome(outcome)}>{outcome}</Button>
                ))}
              </Stack>
              {reviewOutcome === 'present' ? (
                <Stack spacing={1}>
                  {eventDrafts.map((draft, index) => (
                    <Stack key={index} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField label="Event key" value={draft.eventKey} onChange={(event) => setEventDrafts((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, eventKey: event.target.value } : item))} />
                      <TextField label="Start ms" inputMode="numeric" value={draft.startMs} onChange={(event) => setEventDrafts((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, startMs: event.target.value } : item))} />
                      <TextField label="End ms" inputMode="numeric" value={draft.endMs} onChange={(event) => setEventDrafts((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, endMs: event.target.value } : item))} />
                      <TextField label="Subject reference (optional)" value={draft.subjectRef} onChange={(event) => setEventDrafts((items) => items.map((item, itemIndex) => itemIndex === index ? { ...item, subjectRef: event.target.value } : item))} />
                      <Button disabled={eventDrafts.length === 1} onClick={() => setEventDrafts((items) => items.filter((_, itemIndex) => itemIndex !== index))}>Remove</Button>
                    </Stack>
                  ))}
                  <Button sx={{ alignSelf: 'flex-start' }} onClick={() => setEventDrafts((items) => [...items, { eventKey: `event-${items.length + 1}`, startMs: '0', endMs: '1000', subjectRef: '' }])}>Add event</Button>
                </Stack>
              ) : null}
              <Alert severity="info">Submission is immutable. Confirm timing against the playable clip before saving.</Alert>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewItem(null)}>Cancel</Button>
          <Button variant="contained" disabled={working === 'review'} onClick={() => void submitReview()}>Submit immutable annotation</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default MeasurePage;
